import { posthog } from 'posthog-js';

import { signupAttributionProperties } from './signupAttribution';

/**
 * `Signup Completed` — the dashboard end of the marketing site's conversion
 * funnel (Litebox Growth tracking plan, section 9).
 *
 * Contract:
 *  - `window.dataLayer.push({ event: 'signup_completed', method })` for GTM / GA4
 *  - PostHog event `Signup Completed`, Title Case, matching the site's convention
 *  - fires client-side only, exactly once per account
 *  - carries method and consented first/last campaign touches from the website
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const DATA_LAYER_EVENT = 'signup_completed';
const POSTHOG_EVENT = 'Signup Completed';

/** The sign-up options offered on the live sign-up screen */
const OAUTH_METHODS: Record<string, string> = {
  github: 'github',
  google: 'google',
  microsoft: 'microsoft',
};

const EMAIL_METHOD = 'email';

type SignupUser = {
  externalAccounts?: { provider?: string }[];
  verifiedExternalAccounts?: { provider?: string }[];
};

/**
 * How the account was created: `github`, `google`, `microsoft` or `email`.
 *
 * An OAuth provider outside that list is sent verbatim rather than coerced to
 * `email`, so enabling a new SSO option shows up as a new row in PostHog
 * instead of silently inflating the email count.
 */
export const getSignupMethod = (user: SignupUser): string => {
  const provider = user.verifiedExternalAccounts?.[0]?.provider
    ?? user.externalAccounts?.[0]?.provider;

  // no external account: password or email code sign-up
  if (!provider) return EMAIL_METHOD;

  const method = OAUTH_METHODS[provider.toLowerCase()];
  if (!method) {
    console.warn(`Unexpected signup provider "${provider}" — sending it as-is to analytics`);
    return provider.toLowerCase();
  }

  return method;
};

/**
 * Send the signup conversion. Call once per account, never from the backend.
 */
export const trackSignupCompleted = (method: string): void => {
  const properties = { method, ...signupAttributionProperties() };
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event: DATA_LAYER_EVENT, ...properties });

  // posthog is initialised by components/Posthog; EU builds ship without a
  // VITE_POSTHOG_API_KEY, so there is nothing to capture into there
  if (posthog.__loaded) {
    posthog.capture(POSTHOG_EVENT, properties);
  }
};
